<script setup lang="ts">
/** AI: providers (keys are write-only), task routing (text, realtime audio and media tasks), budgets, and what it costs. */
import { Bot, Coins, Database, Download, Loader, Pencil, Plug, Plus, Sigma, Trash2, Upload } from "@lucide/vue";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, h, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { toast } from "vue-sonner";
import {
  createAiProvider,
  deleteAiProvider,
  putAiProvider,
  putAiRoute,
  resetAiRoute,
  testAiProvider,
  useAiProviders,
  useAiRoutes,
  useAiTasks,
  useAiUsage,
  type AiInstanceKind,
  type AiModelDecl,
  type AiProviderConfig,
} from "@/api/admin";
import type { AiProvider, AiRoute } from "@/api/contracts";
import AiBudgetsPanel from "@/components/admin/AiBudgetsPanel.vue";
import AiGatewayPanel from "@/components/admin/AiGatewayPanel.vue";
import AiRulesPanel from "@/components/admin/AiRulesPanel.vue";
import AiRouteForm from "@/components/admin/AiRouteForm.vue";
import { groupRoutes, routeSettings } from "@/components/admin/aiCategories";
import BarChart from "@/components/charts/BarChart.vue";
import BarList from "@/components/charts/BarList.vue";
import DataTable from "@/components/common/DataTable.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import FormError from "@/components/common/FormError.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import PageTabs from "@/components/common/PageTabs.vue";
import SectionCard from "@/components/common/SectionCard.vue";
import SimpleSelect from "@/components/common/SimpleSelect.vue";
import StatCard from "@/components/common/StatCard.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { t } from "@/copy/strings";
import { errorMessage } from "@/lib/errors";
import { formatCompact, formatNumber, formatUsd } from "@/lib/format";
import type { Column } from "@/lib/table";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "AI", requires: { adminArea: "ai" } } });

const TABS = ["routes", "rules", "providers", "gateway", "budgets", "usage"] as const;
const tab = useRouteQuery("tab", "routes");
const tabItems = TABS.map((id) => ({ id, label: t.admin.aiTabs[id] }));
/** The tab shown (an unknown ?tab= falls back to Routing). */
const current = computed(() => ((TABS as readonly string[]).includes(tab.value) ? tab.value : "routes"));
const tabModel = computed({ get: () => current.value, set: (v: string | undefined) => (tab.value = v ?? "routes") });
const queryClient = useQueryClient();
const providers = useAiProviders();
const routes = useAiRoutes();
const tasks = useAiTasks();
const days = ref("30");
// "custom": a range of UTC days (inclusive).
const today = new Date().toISOString().slice(0, 10);
const from = ref(new Date(Date.now() - 29 * 86_400_000).toISOString().slice(0, 10));
const to = ref(today);
const usage = useAiUsage(() =>
  days.value === "custom" && from.value && to.value && from.value <= to.value ? { from: from.value, to: to.value } : Number(days.value === "custom" ? 30 : days.value),
);

const taskInfo = (id: string) => tasks.data.value?.find((x) => x.id === id);
/** Routes per task category (text, embeddings, …, audio for realtime voice, media); a category this build does not know is listed under its id. */
const grouped = computed(() => groupRoutes(routes.data.value ?? []));

function presetsOf(r: AiRoute): string {
  const options = Object.entries(r.options ?? {}).filter(([k]) => k !== "input" && k !== "params" && k !== "systemPrompt");
  return options.map(([k, v]) => `${k}: ${String(v)}`).join(", ") || (r.options?.input ? "custom input" : "—");
}
function openRoute(r: AiRoute) {
  routeError.value = null;
  editing.value = r;
}
const statusBadge = (r: AiRoute) =>
  !r.enabled ? h(Badge, { variant: "warning" }, () => "off") : r.isDefault ? h(Badge, { variant: "muted" }, () => "default") : h(Badge, { variant: "accent" }, () => "customised");

/** Columns of one group's table: reasoning for text-like tasks, presets for media, neither for realtime. */
function routeColumns(group: string): Column<AiRoute>[] {
  const cols: Column<AiRoute>[] = [
    {
      id: "task",
      header: "Task",
      accessorFn: (r) => r.task,
      meta: { mobile: "title" },
      cell: ({ row }) => {
        const info = taskInfo(row.original.task);
        return h("div", { class: "min-w-0" }, [
          h("div", { class: "font-mono font-medium [overflow-wrap:anywhere]" }, row.original.task),
          info && info.title !== row.original.task ? h("div", { class: "text-xs font-normal text-muted-foreground" }, info.title) : null,
        ]);
      },
    },
    {
      id: "model",
      header: "Model",
      accessorFn: (r) => `${r.provider}/${r.model}`,
      meta: { mobile: "subtitle" },
      cell: ({ row }) =>
        h("div", { class: "min-w-0 text-sm whitespace-normal [overflow-wrap:anywhere]" }, [
          h("span", { class: "text-muted-foreground" }, `${row.original.provider} / `),
          h("span", { class: "font-mono text-foreground" }, row.original.model),
        ]),
    },
  ];
  if (routeSettings(group) === "reasoning")
    cols.push({ id: "reasoning", header: "Reasoning", accessorFn: (r) => r.reasoning ?? "", meta: { mobile: "meta" }, cell: ({ row }) => h("span", { class: "text-xs" }, row.original.reasoning ?? "—") });
  else if (routeSettings(group) === "options")
    cols.push({
      id: "presets",
      header: "Presets",
      accessorFn: (r) => presetsOf(r),
      enableSorting: false,
      meta: { mobile: "meta" },
      cell: ({ row }) => h("span", { class: "text-xs whitespace-normal [overflow-wrap:anywhere]" }, presetsOf(row.original)),
    });
  cols.push(
    {
      id: "fallbacks",
      header: "Fallbacks",
      accessorFn: (r) => r.fallbacks.length,
      meta: { mobile: "meta" },
      cell: ({ row }) =>
        row.original.fallbacks.length
          ? h(
              "ol",
              { class: "grid min-w-0 gap-0.5 text-xs whitespace-normal text-muted-foreground" },
              row.original.fallbacks.map((f, i) => h("li", { class: "[overflow-wrap:anywhere]", title: `${f.provider}/${f.model}` }, [`${i + 1}. ${f.provider}/`, h("span", { class: "font-mono" }, f.model)])),
            )
          : h("span", { class: "text-xs text-muted-foreground" }, "—"),
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (r) => (!r.enabled ? "off" : r.isDefault ? "default" : "customised"),
      meta: { mobile: "badge" },
      cell: ({ row }) => statusBadge(row.original),
    },
    {
      id: "edit",
      header: "",
      enableSorting: false,
      meta: { mobile: "actions", align: "end" },
      cell: ({ row }) =>
        h(
          Button,
          { size: "sm", variant: "ghost", "aria-label": `Edit route ${row.original.task}`, onClick: (e: Event) => (e.stopPropagation(), openRoute(row.original)) },
          () => [h(Pencil), t.common.edit],
        ),
    },
  );
  return cols;
}

// --- providers
const testing = ref<string | null>(null);
const testResults = ref<Record<string, { ok: boolean; message: string }>>({});

/** "model-id = chat, vision" per line ↔ declarations. */
const modelsText = (list: AiModelDecl[] | undefined) => (list ?? []).map((m) => `${m.id} = ${m.capabilities.join(", ")}`).join("\n");
function parseModels(text: string): AiModelDecl[] {
  return text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [id, caps] = l.split("=");
      return { id: id.trim(), capabilities: (caps ?? "chat, vision").split(",").map((c) => c.trim()).filter(Boolean) };
    });
}

/** Providers whose models are declared per instance (the API lists `models` for them). */
const declaresModels = (p: AiProvider) => Array.isArray(p.models);
const configOf = (p: AiProvider) => (p.config ?? {}) as Record<string, unknown>;
const str = (v: unknown) => (typeof v === "string" ? v : "");

const INSTANCE_KINDS = [
  { value: "openai-compatible", label: "OpenAI-compatible endpoint", hint: "vLLM, Ollama, LM Studio or any server with /v1/chat/completions." },
  { value: "bedrock", label: "AWS Bedrock", hint: "One region. Claude through inference profiles (eu.anthropic.…), other Converse models, Titan/Cohere embeddings." },
  { value: "cloudflare", label: "Cloudflare", hint: "Workers AI and AI Gateway of one account (API token)." },
];
const BEDROCK_AUTH = [
  { value: "key", label: "Bedrock API key", hint: "Bearer token; AWS_BEARER_TOKEN_BEDROCK bootstraps it." },
  { value: "iam", label: "AWS identity", hint: "SigV4 with smake-mcp's own role (IRSA / credential chain); needs bedrock:InvokeModel." },
];
const CLOUDFLARE_MODES = [
  { value: "workers-ai", label: "Workers AI REST API", hint: "@cf/… models and author/model (Unified Billing); a gateway adds its logs, cache and limits." },
  { value: "ai-gateway", label: "AI Gateway (compat)", hint: "provider/model, dynamic/<route> or cloudflare/auto through the gateway (default: \"default\")." },
];
/** Options for SimpleSelect (labels only: the hint goes below the select). */
const labelsOf = (list: { value: string; label: string }[]) => list.map(({ value, label }) => ({ value, label }));
const hintOf = (list: { value: string; hint: string }[], value: string) => list.find((o) => o.value === value)?.hint;
const MODEL_PLACEHOLDER: Record<string, string> = {
  "openai-compatible": "Qwen/Qwen3-VL-8B-Instruct = chat, vision\nallenai/olmOCR-2-7B-1025 = vision",
  bedrock: "eu.anthropic.claude-sonnet-5-5 = chat, vision\namazon.titan-embed-text-v2:0 = embed",
  cloudflare: "@cf/openai/gpt-oss-120b = chat\nanthropic/claude-sonnet-5-5 = chat, vision\n@cf/baai/bge-m3 = embed",
};

/** The editable fields of a provider (add and edit dialogs). */
interface ProviderForm {
  id: string;
  kind: string;
  apiKey: string;
  baseUrl: string;
  models: string;
  region: string;
  auth: string;
  accountId: string;
  gatewayId: string;
  mode: string;
  /** Number inputs give numbers (v-model casts them). */
  rpm: string | number;
}

function formFor(kind: string, p?: AiProvider): ProviderForm {
  const c = p ? configOf(p) : {};
  return {
    id: p?.id ?? "",
    kind,
    apiKey: "",
    baseUrl: p?.baseUrl ?? "",
    models: modelsText(p?.models),
    region: str(c.region),
    auth: str(c.auth) || "key",
    accountId: str(c.accountId),
    gatewayId: str(c.gatewayId),
    mode: str(c.mode) || "workers-ai",
    rpm: typeof c.rpm === "number" ? String(c.rpm) : "",
  };
}

/** The config to store: the prior settings (other keys kept) with this form's fields set or cleared. */
function configFrom(f: ProviderForm, prior: Record<string, unknown> = {}): AiProviderConfig {
  const out: AiProviderConfig = {};
  for (const [k, v] of Object.entries(prior)) if (v === null || ["string", "number", "boolean"].includes(typeof v)) out[k] = v as string | number | boolean | null;
  const set = (key: string, value: string | number | undefined) => {
    if (value === undefined || value === "") delete out[key];
    else out[key] = value;
  };
  if (f.kind === "bedrock") {
    set("region", f.region.trim());
    set("auth", f.auth === "iam" ? "iam" : undefined);
  }
  if (f.kind === "cloudflare") {
    set("accountId", f.accountId.trim());
    set("gatewayId", f.gatewayId.trim());
    set("mode", f.mode === "ai-gateway" ? "ai-gateway" : undefined);
  }
  const rpm = Number(f.rpm);
  set("rpm", String(f.rpm).trim() && Number.isFinite(rpm) && rpm > 0 ? Math.round(rpm) : undefined);
  return out;
}

/** One line under the provider name: where it points and its limits. */
function configSummary(p: AiProvider): string {
  const c = configOf(p);
  const parts: string[] = [];
  if (p.kind === "bedrock") parts.push(str(c.region) || "eu-central-1", c.auth === "iam" ? "AWS identity" : "API key");
  if (p.kind === "cloudflare") {
    parts.push(c.accountId ? `account ${str(c.accountId)}` : "no account id");
    parts.push(c.mode === "ai-gateway" ? `AI Gateway ${str(c.gatewayId) || "default"}` : c.gatewayId ? `Workers AI via gateway ${str(c.gatewayId)}` : "Workers AI");
  }
  if (typeof c.rpm === "number") parts.push(`≤ ${c.rpm} requests/min`);
  return parts.join(" · ");
}

/** Bedrock on the AWS identity needs no key. */
const usesIdentity = (p: AiProvider) => p.kind === "bedrock" && configOf(p).auth === "iam";

async function toggleProvider(p: AiProvider, enabled: boolean) {
  try {
    await putAiProvider(p.id, { enabled });
    await queryClient.invalidateQueries({ queryKey: ["admin", "ai"] });
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

/** smake-peer instances come from their datasource (key, base URL, models). */
function peerDatasource(p: AiProvider): string | undefined {
  const x = p as AiProvider & { keySource?: string; config?: Record<string, unknown> };
  return x.keySource === "datasource" && typeof x.config?.datasource === "string" ? x.config.datasource : undefined;
}

async function test(p: AiProvider) {
  testing.value = p.id;
  try {
    testResults.value = { ...testResults.value, [p.id]: await testAiProvider(p.id) };
  } catch (err) {
    testResults.value = { ...testResults.value, [p.id]: { ok: false, message: errorMessage(err) } };
  } finally {
    testing.value = null;
  }
}

// --- add or edit a provider (key, base URL, declared models, settings)
const dialog = ref<{ mode: "add" | "edit"; provider?: AiProvider } | null>(null);
const dialogOpen = computed({ get: () => Boolean(dialog.value), set: (v) => !v && (dialog.value = null) });
const form = ref<ProviderForm>(formFor("openai-compatible"));
const formError = ref<unknown>(null);
const editedProvider = computed(() => dialog.value?.provider);

function openAdd() {
  formError.value = null;
  form.value = formFor("openai-compatible");
  dialog.value = { mode: "add" };
}
function openEdit(p: AiProvider) {
  formError.value = null;
  form.value = formFor(p.kind, p);
  dialog.value = { mode: "edit", provider: p };
}
// Adding: a new kind starts from its defaults (id and key stay).
watch(
  () => form.value.kind,
  (kind, prev) => {
    if (dialog.value?.mode === "add" && kind !== prev) form.value = { ...formFor(kind), id: form.value.id, apiKey: form.value.apiKey };
  },
);

const formReady = computed(() => {
  const f = form.value;
  if (dialog.value?.mode !== "add") return true;
  if (!/^[a-z][a-z0-9-]*$/.test(f.id)) return false;
  if (f.kind === "openai-compatible") return Boolean(f.baseUrl);
  if (f.kind === "cloudflare") return Boolean(f.accountId && f.apiKey);
  return true;
});
const showKey = computed(() => !(form.value.kind === "bedrock" && form.value.auth === "iam" && !editedProvider.value?.hasKey));
const showBaseUrl = computed(() => form.value.kind !== "cloudflare" && form.value.kind !== "smake-peer");
const showModels = computed(() => (editedProvider.value ? declaresModels(editedProvider.value) : true));
const keyLabel = computed(() => (form.value.kind === "cloudflare" ? "API token" : form.value.kind === "bedrock" ? "Bedrock API key" : "API key"));
const baseUrlHint = computed(() =>
  form.value.kind === "openai-compatible" ? "http(s), e.g. http://vllm:8000/v1" : form.value.kind === "bedrock" ? "optional, a VPC endpoint" : "optional, https",
);

async function saveProvider(removeKey = false) {
  const f = form.value;
  formError.value = null;
  try {
    if (dialog.value?.mode === "add") {
      const config = configFrom(f);
      await createAiProvider({
        id: f.id.trim(),
        kind: f.kind as AiInstanceKind,
        ...(f.baseUrl.trim() && showBaseUrl.value ? { baseUrl: f.baseUrl.trim() } : {}),
        apiKey: f.apiKey.trim() || null,
        models: parseModels(f.models),
        ...(Object.keys(config).length ? { config } : {}),
      });
      toast.success(`Provider ${f.id} added.`);
    } else {
      const p = editedProvider.value;
      if (!p) return;
      const body: Parameters<typeof putAiProvider>[1] = {};
      if (removeKey) body.apiKey = null;
      else if (f.apiKey) body.apiKey = f.apiKey;
      if (f.baseUrl !== (p.baseUrl ?? "")) body.baseUrl = f.baseUrl || null;
      if (declaresModels(p)) body.models = parseModels(f.models);
      const config = configFrom(f, configOf(p));
      if (JSON.stringify(config) !== JSON.stringify(configOf(p))) body.config = config;
      await putAiProvider(p.id, body);
      toast.success(removeKey ? "Key removed." : "Saved.");
    }
    dialog.value = null;
    await queryClient.invalidateQueries({ queryKey: ["admin", "ai"] });
  } catch (err) {
    formError.value = err;
  }
}

async function removeProvider(p: AiProvider) {
  if (!window.confirm(`Remove ${p.id}? Routes that use it go back to their defaults.`)) return;
  try {
    await deleteAiProvider(p.id);
    toast.success(`${p.id} removed.`);
    await queryClient.invalidateQueries({ queryKey: ["admin", "ai"] });
  } catch (err) {
    toast.error(errorMessage(err));
  }
}

// --- routes
const editing = ref<AiRoute | null>(null);
const routeOpen = computed({ get: () => Boolean(editing.value), set: (v) => !v && (editing.value = null) });
const busy = ref(false);
const routeError = ref<unknown>(null);

async function saveRoute(value: Omit<AiRoute, "task" | "isDefault" | "category">) {
  if (!editing.value) return;
  busy.value = true;
  routeError.value = null;
  try {
    await putAiRoute(editing.value.task, value);
    toast.success(`Route ${editing.value.task} saved.`);
    editing.value = null;
    await queryClient.invalidateQueries({ queryKey: ["admin", "ai", "routes"] });
  } catch (err) {
    routeError.value = err;
  } finally {
    busy.value = false;
  }
}

async function reset() {
  if (!editing.value) return;
  busy.value = true;
  try {
    await resetAiRoute(editing.value.task);
    toast.success("Back to the default.");
    editing.value = null;
    await queryClient.invalidateQueries({ queryKey: ["admin", "ai", "routes"] });
  } catch (err) {
    routeError.value = err;
  } finally {
    busy.value = false;
  }
}

// Deep link from models_explore: /admin/ai?tab=routes&task=image-upscale&provider=replicate&model=owner/name
const route = useRoute();
const router = useRouter();
function openFromQuery() {
  const task = typeof route.query.task === "string" ? route.query.task : undefined;
  const r = task ? routes.data.value?.find((x) => x.task === task) : undefined;
  if (!r) return;
  const provider = typeof route.query.provider === "string" ? route.query.provider : undefined;
  const model = typeof route.query.model === "string" ? route.query.model : undefined;
  routeError.value = null;
  editing.value = provider && model ? { ...r, provider, model, options: undefined } : r;
  void router.replace({ query: { tab: "routes" } });
}
onMounted(openFromQuery);
watch(() => routes.data.value, openFromQuery);

const usageDays = computed(() => {
  const u = usage.data.value as ({ range?: { days?: number } } & NonNullable<typeof usage.data.value>) | undefined;
  return u?.range?.days ?? u?.byDay.length ?? 0;
});
const usageCalls = computed(() => {
  const u = usage.data.value;
  if (!u) return 0;
  const totals = u.totals as typeof u.totals & { calls?: number };
  return totals.calls ?? u.byTask.reduce((n, x) => n + x.calls, 0);
});

/**
 * Per-day and per-call estimates: a call usually costs a fraction of a cent, so
 * below 10 cents they keep two significant digits ("$0.0032", not "$0.00");
 * from $0.095 whole cents ("$0.10") say as much.
 */
const smallUsd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumSignificantDigits: 2 });
const formatEstimate = (n: number) => (n > 0 && n < 0.095 ? smallUsd.format(n) : formatUsd(n));

const dayLabel = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
</script>

<template>
  <div>
    <PageHeader :crumbs="[{ label: t.admin.title, to: '/admin' }]" :title="t.admin.ai" :description="t.admin.aiDescription">
      <template #tabs><PageTabs v-model="tabModel" :items="tabItems" :label="t.admin.ai" id-prefix="ai-tab" /></template>
    </PageHeader>

    <div :id="`ai-panel-${current}`" role="tabpanel" :aria-labelledby="`ai-tab-${current}`" class="min-w-0">

      <!-- Routing -->
      <template v-if="current === 'routes'">
        <ListSkeleton v-if="routes.isPending.value" :rows="6" />
        <ErrorState v-else-if="routes.error.value" :error="routes.error.value" @retry="routes.refetch()" />
        <EmptyState v-else-if="!routes.data.value?.length" :icon="Bot" title="No AI tasks registered" />
        <div v-else class="flex flex-col gap-6">
          <section v-for="g in grouped" :key="g.id" :aria-labelledby="`cat-${g.id}`" class="min-w-0">
            <h2 :id="`cat-${g.id}`" class="section-title mb-2 flex items-baseline gap-2">
              {{ g.label }} <span class="text-xs font-normal text-muted-foreground tabular-nums">{{ g.routes.length }}</span>
            </h2>
            <DataTable :data="g.routes" :columns="routeColumns(g.id)" :row-key="(r) => r.task" row-clickable dense :caption="`${g.label} routes`" @row-click="openRoute" />
          </section>
        </div>
      </template>

      <!-- Providers -->
      <template v-else-if="current === 'providers'">
        <ListSkeleton v-if="providers.isPending.value" :rows="4" />
        <ErrorState v-else-if="providers.error.value" :error="providers.error.value" @retry="providers.refetch()" />
        <div v-else-if="providers.data.value" class="mb-3 flex justify-end">
          <Button size="sm" variant="outline" @click="openAdd"><Plus /> Add provider</Button>
        </div>
        <ul v-if="providers.data.value" class="grid gap-3 md:grid-cols-2">
          <li v-for="p in providers.data.value" :key="p.id" class="surface flex min-w-0 flex-col gap-3 p-4">
            <div class="flex min-w-0 items-center gap-2">
              <span class="min-w-0 truncate font-medium">{{ p.id }}</span>
              <span class="shrink-0 text-xs text-muted-foreground">{{ p.kind }}</span>
              <Badge v-if="p.hasKey" variant="success" class="ml-auto">{{ p.keySource === "datasource" ? "key from datasource" : "key set" }}</Badge>
              <Badge v-else-if="p.keyless" variant="muted" class="ml-auto">no key needed</Badge>
              <Badge v-else-if="usesIdentity(p)" variant="muted" class="ml-auto">AWS identity</Badge>
              <Badge v-else variant="warning" class="ml-auto">no key</Badge>
              <Switch :model-value="p.enabled" :aria-label="`${p.id} enabled`" @update:model-value="(v: boolean) => toggleProvider(p, v)" />
            </div>
            <p v-if="configSummary(p)" class="text-xs text-muted-foreground">{{ configSummary(p) }}</p>
            <p v-if="p.baseUrl" class="truncate font-mono text-xs text-muted-foreground">{{ p.baseUrl }}</p>
            <p v-if="p.models?.length" class="text-xs [overflow-wrap:anywhere] text-muted-foreground">
              Models: <span v-for="(m, i) in p.models" :key="m.id"><span class="font-mono">{{ m.id }}</span> ({{ m.capabilities.join(", ") }}){{ i < p.models.length - 1 ? "; " : "" }}</span>
            </p>
            <p v-if="testResults[p.id]" role="status" :class="cn('rounded-md px-2.5 py-1.5 text-xs', testResults[p.id].ok ? 'bg-success-soft text-success' : 'bg-destructive-soft text-destructive')">
              {{ testResults[p.id].message }}
            </p>
            <div class="mt-auto flex flex-wrap gap-2">
              <Button v-if="peerDatasource(p)" as-child size="sm" variant="outline"><RouterLink :to="`/admin/datasources/smake-peer/${peerDatasource(p)}`"><Database /> Datasource</RouterLink></Button>
              <Button size="sm" variant="outline" @click="openEdit(p)"><Pencil /> Edit</Button>
              <Button size="sm" variant="ghost" :disabled="testing === p.id" @click="test(p)"><Loader v-if="testing === p.id" class="animate-spin" /><Plug v-else /> {{ t.common.test }}</Button>
              <Button v-if="p.removable" size="sm" variant="ghost" class="ml-auto text-destructive" :aria-label="`Remove ${p.id}`" @click="removeProvider(p)"><Trash2 /></Button>
            </div>
          </li>
        </ul>
      </template>

      <!-- Budgets -->
      <AiBudgetsPanel v-else-if="current === 'budgets'" />

      <!-- Rules and gateway (docs/v2/ai-gateway.md) -->
      <AiRulesPanel v-else-if="current === 'rules'" />
      <AiGatewayPanel v-else-if="current === 'gateway'" />

      <!-- Usage -->
      <template v-else>
        <div class="mb-4 flex flex-wrap items-center justify-end gap-2">
          <template v-if="days === 'custom'">
            <Input v-model="from" type="date" class="w-auto" aria-label="From (UTC day)" :max="to" />
            <span class="text-sm text-muted-foreground">to</span>
            <Input v-model="to" type="date" class="w-auto" aria-label="To (UTC day, inclusive)" :min="from" :max="today" />
          </template>
          <ToggleGroup :model-value="days" type="single" variant="outline" size="sm" aria-label="Period" @update:model-value="(v) => v && (days = String(v))">
            <ToggleGroupItem value="7">7 days</ToggleGroupItem>
            <ToggleGroupItem value="30">30 days</ToggleGroupItem>
            <ToggleGroupItem value="90">90 days</ToggleGroupItem>
            <ToggleGroupItem value="custom">Range</ToggleGroupItem>
          </ToggleGroup>
        </div>
        <ListSkeleton v-if="usage.isPending.value" :rows="6" />
        <ErrorState v-else-if="usage.error.value" :error="usage.error.value" @retry="usage.refetch()" />
        <div v-else-if="usage.data.value" class="flex flex-col gap-6">
          <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatCard
              label="Cost"
              :value="formatUsd(usage.data.value.totals.costUsd)"
              :icon="Coins"
              :hint="usageDays ? `≈ ${formatEstimate(usage.data.value.totals.costUsd / usageDays)} per day` : undefined"
            />
            <StatCard label="Calls" :value="formatNumber(usageCalls)" :icon="Sigma" :hint="usageCalls ? `≈ ${formatEstimate(usage.data.value.totals.costUsd / usageCalls)} per call` : undefined" />
            <StatCard label="Input tokens" :value="formatCompact(usage.data.value.totals.inputTokens)" :icon="Upload" :hint="`${formatNumber(usage.data.value.totals.inputTokens)} tokens`" />
            <StatCard label="Output tokens" :value="formatCompact(usage.data.value.totals.outputTokens)" :icon="Download" :hint="`${formatNumber(usage.data.value.totals.outputTokens)} tokens`" />
          </div>
          <SectionCard title="Cost per day">
            <BarChart :data="usage.data.value.byDay.map((d) => ({ label: dayLabel(d.day), value: d.costUsd }))" :format="formatUsd" title="Cost per day in US dollars" />
          </SectionCard>
          <div class="grid gap-6 lg:grid-cols-3">
            <SectionCard title="Top spenders">
              <BarList :items="usage.data.value.topSpenders.map((x) => ({ label: x.username, value: x.costUsd, note: `${formatNumber(x.calls)} calls` }))" :format="formatUsd" :limit="10" />
            </SectionCard>
            <SectionCard title="By role" description="Current members; a person in two roles counts in both.">
              <BarList :items="usage.data.value.byRole.map((x) => ({ label: x.role, value: x.costUsd, note: `${formatNumber(x.people)} people` }))" :format="formatUsd" />
            </SectionCard>
            <SectionCard title="By category">
              <BarList :items="usage.data.value.byCategory.map((x) => ({ label: x.category, value: x.costUsd, note: `${formatNumber(x.calls)} calls` }))" :format="formatUsd" />
            </SectionCard>
            <SectionCard title="By task"><BarList :items="usage.data.value.byTask.map((x) => ({ label: x.task, value: x.costUsd, note: `${formatNumber(x.calls)} calls` }))" :format="formatUsd" /></SectionCard>
            <SectionCard title="By model"><BarList :items="usage.data.value.byModel.map((x) => ({ label: x.model, value: x.costUsd, note: `${formatNumber(x.calls)} calls` }))" :format="formatUsd" /></SectionCard>
            <SectionCard title="By person (incl. system)"><BarList :items="usage.data.value.byUser.map((x) => ({ label: x.username, value: x.costUsd }))" :format="formatUsd" /></SectionCard>
          </div>
        </div>
      </template>

    </div>

    <Sheet v-model:open="routeOpen">
      <SheetContent side="right" class="w-full overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>Route: {{ editing?.task }}</SheetTitle>
          <SheetDescription>Applies to new requests immediately. Budgets still apply.</SheetDescription>
        </SheetHeader>
        <div class="px-4 pb-6">
          <AiRouteForm v-if="editing" :key="`${editing.task}:${editing.provider}:${editing.model}`" :route="editing" :task="taskInfo(editing.task)" :providers="providers.data.value ?? []" :busy="busy" :error="routeError" @submit="saveRoute" @reset="reset" @cancel="editing = null" />
        </div>
      </SheetContent>
    </Sheet>

    <Dialog v-model:open="dialogOpen">
      <DialogContent class="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{{ dialog?.mode === "add" ? "Add provider" : `${editedProvider?.id} settings` }}</DialogTitle>
          <DialogDescription>
            Keys are stored encrypted and can't be shown again.
            <template v-if="showModels"> Routes only use declared models, for what they were declared.</template>
          </DialogDescription>
        </DialogHeader>
        <form class="grid gap-4" @submit.prevent="saveProvider()">
          <template v-if="dialog?.mode === 'add'">
            <div class="grid gap-1.5">
              <Label for="pv-kind">Kind</Label>
              <SimpleSelect id="pv-kind" v-model="form.kind" :options="labelsOf(INSTANCE_KINDS)" aria-label="Provider kind" />
              <p class="text-xs text-muted-foreground">{{ hintOf(INSTANCE_KINDS, form.kind) }}</p>
            </div>
            <div class="grid gap-1.5">
              <Label for="pv-id">Id</Label>
              <Input id="pv-id" v-model="form.id" :placeholder="form.kind === 'bedrock' ? 'bedrock-eu' : form.kind === 'cloudflare' ? 'cloudflare' : 'vllm-gpu'" pattern="[a-z][a-z0-9-]*" required />
            </div>
          </template>

          <template v-if="form.kind === 'bedrock'">
            <div class="grid gap-4 sm:grid-cols-2">
              <div class="grid gap-1.5">
                <Label for="pv-region">Region</Label>
                <Input id="pv-region" v-model="form.region" placeholder="eu-central-1" pattern="[a-z]{2}(-[a-z]+)+-[0-9]" />
              </div>
              <div class="grid gap-1.5">
                <Label for="pv-auth">Authentication</Label>
                <SimpleSelect id="pv-auth" v-model="form.auth" :options="labelsOf(BEDROCK_AUTH)" aria-label="Authentication" />
              </div>
            </div>
            <p class="-mt-2 text-xs text-muted-foreground">{{ hintOf(BEDROCK_AUTH, form.auth) }}</p>
          </template>

          <template v-if="form.kind === 'cloudflare'">
            <div class="grid gap-1.5">
              <Label for="pv-account">Account id</Label>
              <Input id="pv-account" v-model="form.accountId" class="font-mono" placeholder="32-character account id" :required="dialog?.mode === 'add'" />
            </div>
            <div class="grid gap-4 sm:grid-cols-2">
              <div class="grid gap-1.5">
                <Label for="pv-mode">Endpoint</Label>
                <SimpleSelect id="pv-mode" v-model="form.mode" :options="labelsOf(CLOUDFLARE_MODES)" aria-label="Endpoint" />
              </div>
              <div class="grid gap-1.5">
                <Label for="pv-gateway">AI Gateway <span class="font-normal text-muted-foreground">(optional)</span></Label>
                <Input id="pv-gateway" v-model="form.gatewayId" :placeholder="form.mode === 'ai-gateway' ? 'default' : 'none (direct)'" />
              </div>
            </div>
            <p class="-mt-2 text-xs text-muted-foreground">{{ hintOf(CLOUDFLARE_MODES, form.mode) }}</p>
          </template>

          <div v-if="showKey" class="grid gap-1.5">
            <Label for="pv-key">
              {{ keyLabel }}
              <span v-if="form.kind === 'openai-compatible' || form.kind === 'bedrock'" class="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="pv-key"
              v-model="form.apiKey"
              type="password"
              autocomplete="new-password"
              :placeholder="editedProvider?.hasKey ? '•••••••• stored — type to replace' : editedProvider?.keySource === 'datasource' ? 'From the datasource' : 'Paste the key'"
            />
          </div>

          <div v-if="showBaseUrl" class="grid gap-1.5">
            <Label for="pv-base">Base URL <span class="font-normal text-muted-foreground">({{ baseUrlHint }})</span></Label>
            <Input id="pv-base" v-model="form.baseUrl" type="url" :placeholder="form.kind === 'openai-compatible' ? 'http://vllm.ai.svc:8000/v1' : 'https://…'" :required="dialog?.mode === 'add' && form.kind === 'openai-compatible'" />
          </div>

          <div v-if="showModels" class="grid gap-1.5">
            <Label for="pv-models">Models <span class="font-normal text-muted-foreground">(one per line: id = chat, vision, embed, image-generate)</span></Label>
            <Textarea id="pv-models" v-model="form.models" rows="4" class="font-mono text-xs" :placeholder="MODEL_PLACEHOLDER[form.kind] ?? ''" />
            <p v-if="form.kind === 'bedrock' || form.kind === 'cloudflare'" class="text-xs text-muted-foreground">Empty: the catalogue's models of this kind (Claude inference profiles, embeddings).</p>
          </div>

          <div class="grid gap-1.5">
            <Label for="pv-rpm">Requests per minute <span class="font-normal text-muted-foreground">(optional limit)</span></Label>
            <Input id="pv-rpm" v-model="form.rpm" type="number" min="1" step="1" class="w-40" placeholder="no limit" />
          </div>

          <FormError :error="formError" />
          <DialogFooter class="gap-2">
            <Button v-if="editedProvider?.hasKey && editedProvider.keySource !== 'datasource'" type="button" variant="ghost" class="text-destructive sm:mr-auto" @click="saveProvider(true)">Remove key</Button>
            <Button type="button" variant="ghost" @click="dialog = null">{{ t.common.cancel }}</Button>
            <Button type="submit" :disabled="!formReady">{{ t.common.save }}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  </div>
</template>
